/**
 * SpontanBookingPage - Book a slot from waitlist notification
 * Accessible via /spontan-buchen?token=xxx&lang=de|en|fr|it
 * 07/2026: mehrsprachig (de/en/fr/it)
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, Clock, User, Check, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const T = {
  de: {
    locale: 'de-CH',
    loading: 'Lade Termininfos...',
    notAvailable: 'Termin nicht verfügbar',
    errInvalid: 'Dieser Buchungslink ist nicht mehr gültig oder der Termin wurde bereits gebucht.',
    errExpired: 'Dieser Link ist abgelaufen.',
    errLoad: 'Fehler beim Laden der Termininfos',
    errTaken: 'Dieser Termin wurde bereits von jemand anderem gebucht.',
    errSeriesGone: 'Die Serie ist nicht mehr verfügbar.',
    errBooking: 'Fehler bei der Buchung. Bitte versuche es erneut.',
    errSeriesBooking: 'Fehler bei der Serien-Buchung. Bitte versuche es erneut.',
    toPortal: 'Zum Kundenportal',
    bookedTitle: 'Termin gebucht!',
    bookedSeriesTitle: 'Serie gebucht!',
    bookedText: (s) => `Dein Termin für ${s} wurde erfolgreich gebucht.`,
    bookedSeriesText: (n, s) => `${n} Termine für ${s} wurden erfolgreich gebucht.`,
    skipped: (n) => `${n} Termin(e) waren bereits belegt und wurden übersprungen.`,
    every: (wd, tm) => `Jeden ${wd} um ${tm} Uhr`,
    at: (d, t) => `${d} um ${t} Uhr`,
    redirect: 'Du wirst in 3 Sekunden zum Kundenportal weitergeleitet...',
    nowToPortal: 'Jetzt zum Portal',
    title: 'Spontaner Termin verfügbar!',
    subtitle: 'Ein Termin ist kurzfristig frei geworden',
    date: 'Datum', time: 'Uhrzeit', timeSuffix: ' Uhr', service: 'Service',
    minutes: 'Minuten',
    urgency: '⚡ Schnell sein! Wer zuerst bucht, bekommt den Termin.',
    seriesTitle: '🔁 Dauerhaft frei — ganze Serie verfügbar!',
    seriesText: (p, n) => `${p} — ${n} Termine. Sichere dir jetzt deinen festen, wiederkehrenden Termin mit einem Klick.`,
    seriesFallback: 'Dieser Termin ist regelmäßig frei',
    loginNotice: 'Du musst dich anmelden, um diesen Termin zu buchen.',
    bookingBusy: 'Wird gebucht...',
    bookNow: 'Jetzt buchen',
    onlyThis: 'Nur diesen Termin buchen',
    loginAndBook: 'Anmelden & Buchen',
    bookSeries: (n) => `🔁 Ganze Serie buchen (${n} Termine)`,
    validUntil: 'Dieser Link ist gültig bis:',
    toastBooked: 'Termin erfolgreich gebucht!',
    toastSeries: 'Serie erfolgreich gebucht!',
  },
  en: {
    locale: 'en-GB',
    loading: 'Loading appointment details...',
    notAvailable: 'Appointment not available',
    errInvalid: 'This booking link is no longer valid or the slot has already been booked.',
    errExpired: 'This link has expired.',
    errLoad: 'Error loading appointment details',
    errTaken: 'This slot has already been booked by someone else.',
    errSeriesGone: 'The series is no longer available.',
    errBooking: 'Booking error. Please try again.',
    errSeriesBooking: 'Series booking error. Please try again.',
    toPortal: 'Go to customer portal',
    bookedTitle: 'Appointment booked!',
    bookedSeriesTitle: 'Series booked!',
    bookedText: (s) => `Your appointment for ${s} has been booked successfully.`,
    bookedSeriesText: (n, s) => `${n} appointments for ${s} have been booked successfully.`,
    skipped: (n) => `${n} appointment(s) were already taken and were skipped.`,
    every: (wd, tm) => `Every ${wd} at ${tm}`,
    at: (d, t) => `${d} at ${t}`,
    redirect: 'You will be redirected to the customer portal in 3 seconds...',
    nowToPortal: 'Go to portal now',
    title: 'Last-minute slot available!',
    subtitle: 'A slot has just become available',
    date: 'Date', time: 'Time', timeSuffix: '', service: 'Service',
    minutes: 'minutes',
    urgency: '⚡ Be quick! First come, first served.',
    seriesTitle: '🔁 Permanently available — whole series free!',
    seriesText: (p, n) => `${p} — ${n} sessions. Secure your fixed recurring appointment now with one click.`,
    seriesFallback: 'This slot is regularly available',
    loginNotice: 'You need to sign in to book this slot.',
    bookingBusy: 'Booking...',
    bookNow: 'Book now',
    onlyThis: 'Book this slot only',
    loginAndBook: 'Sign in & book',
    bookSeries: (n) => `🔁 Book whole series (${n} sessions)`,
    validUntil: 'This link is valid until:',
    toastBooked: 'Appointment booked successfully!',
    toastSeries: 'Series booked successfully!',
  },
  fr: {
    locale: 'fr-CH',
    loading: 'Chargement des informations...',
    notAvailable: 'Rendez-vous non disponible',
    errInvalid: "Ce lien de réservation n'est plus valable ou le créneau a déjà été réservé.",
    errExpired: 'Ce lien a expiré.',
    errLoad: 'Erreur lors du chargement des informations',
    errTaken: "Ce créneau a déjà été réservé par quelqu'un d'autre.",
    errSeriesGone: "La série n'est plus disponible.",
    errBooking: 'Erreur de réservation. Réessaie, s\'il te plaît.',
    errSeriesBooking: 'Erreur lors de la réservation de la série. Réessaie, s\'il te plaît.',
    toPortal: 'Vers le portail client',
    bookedTitle: 'Rendez-vous réservé !',
    bookedSeriesTitle: 'Série réservée !',
    bookedText: (s) => `Ton rendez-vous pour ${s} a été réservé avec succès.`,
    bookedSeriesText: (n, s) => `${n} rendez-vous pour ${s} ont été réservés avec succès.`,
    skipped: (n) => `${n} rendez-vous étaient déjà pris et ont été ignorés.`,
    every: (wd, tm) => `Chaque ${wd} à ${tm}`,
    at: (d, t) => `${d} à ${t}`,
    redirect: 'Tu seras redirigé vers le portail client dans 3 secondes...',
    nowToPortal: 'Vers le portail',
    title: 'Créneau disponible !',
    subtitle: "Un créneau vient de se libérer",
    date: 'Date', time: 'Heure', timeSuffix: '', service: 'Service',
    minutes: 'minutes',
    urgency: '⚡ Fais vite ! Premier arrivé, premier servi.',
    seriesTitle: '🔁 Disponible en permanence — toute la série est libre !',
    seriesText: (p, n) => `${p} — ${n} séances. Réserve ton rendez-vous fixe récurrent en un clic.`,
    seriesFallback: 'Ce créneau est régulièrement libre',
    loginNotice: 'Tu dois te connecter pour réserver ce créneau.',
    bookingBusy: 'Réservation...',
    bookNow: 'Réserver',
    onlyThis: 'Réserver uniquement ce rendez-vous',
    loginAndBook: 'Se connecter & réserver',
    bookSeries: (n) => `🔁 Réserver toute la série (${n} séances)`,
    validUntil: "Ce lien est valable jusqu'au :",
    toastBooked: 'Rendez-vous réservé avec succès !',
    toastSeries: 'Série réservée avec succès !',
  },
  it: {
    locale: 'it-CH',
    loading: 'Caricamento informazioni...',
    notAvailable: 'Appuntamento non disponibile',
    errInvalid: 'Questo link di prenotazione non è più valido o lo slot è già stato prenotato.',
    errExpired: 'Questo link è scaduto.',
    errLoad: 'Errore durante il caricamento delle informazioni',
    errTaken: 'Questo slot è già stato prenotato da qualcun altro.',
    errSeriesGone: 'La serie non è più disponibile.',
    errBooking: 'Errore di prenotazione. Riprova.',
    errSeriesBooking: 'Errore nella prenotazione della serie. Riprova.',
    toPortal: 'Al portale clienti',
    bookedTitle: 'Appuntamento prenotato!',
    bookedSeriesTitle: 'Serie prenotata!',
    bookedText: (s) => `Il tuo appuntamento per ${s} è stato prenotato con successo.`,
    bookedSeriesText: (n, s) => `${n} appuntamenti per ${s} sono stati prenotati con successo.`,
    skipped: (n) => `${n} appuntamento/i erano già occupati e sono stati saltati.`,
    every: (wd, tm) => `Ogni ${wd} alle ${tm}`,
    at: (d, t) => `${d} alle ${t}`,
    redirect: 'Verrai reindirizzato al portale clienti tra 3 secondi...',
    nowToPortal: 'Vai al portale',
    title: 'Appuntamento disponibile!',
    subtitle: 'Uno slot si è appena liberato',
    date: 'Data', time: 'Ora', timeSuffix: '', service: 'Servizio',
    minutes: 'minuti',
    urgency: '⚡ Fai presto! Chi prima prenota, prima ottiene.',
    seriesTitle: '🔁 Sempre disponibile — intera serie libera!',
    seriesText: (p, n) => `${p} — ${n} appuntamenti. Assicurati subito il tuo appuntamento fisso ricorrente con un clic.`,
    seriesFallback: 'Questo slot è regolarmente libero',
    loginNotice: 'Devi accedere per prenotare questo slot.',
    bookingBusy: 'Prenotazione...',
    bookNow: 'Prenota ora',
    onlyThis: 'Prenota solo questo appuntamento',
    loginAndBook: 'Accedi & prenota',
    bookSeries: (n) => `🔁 Prenota tutta la serie (${n} appuntamenti)`,
    validUntil: 'Questo link è valido fino al:',
    toastBooked: 'Appuntamento prenotato con successo!',
    toastSeries: 'Serie prenotata con successo!',
  },
};

const WEEKDAY_I18N = {
  Montag: { en: 'Monday', fr: 'lundi', it: 'lunedì' },
  Dienstag: { en: 'Tuesday', fr: 'mardi', it: 'martedì' },
  Mittwoch: { en: 'Wednesday', fr: 'mercredi', it: 'mercoledì' },
  Donnerstag: { en: 'Thursday', fr: 'jeudi', it: 'giovedì' },
  Freitag: { en: 'Friday', fr: 'vendredi', it: 'venerdì' },
  Samstag: { en: 'Saturday', fr: 'samedi', it: 'sabato' },
  Sonntag: { en: 'Sunday', fr: 'dimanche', it: 'domenica' },
};

const resolveLang = (paramLang) => {
  const supported = ['de', 'en', 'fr', 'it'];
  if (paramLang && supported.includes(paramLang)) return paramLang;
  try {
    const pc = JSON.parse(localStorage.getItem('portal_customer') || 'null');
    if (pc?.preferred_language && supported.includes(pc.preferred_language)) return pc.preferred_language;
  } catch (e) { /* ignore */ }
  const nav = (navigator.language || 'de').slice(0, 2);
  return supported.includes(nav) ? nav : 'de';
};

const SpontanBookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const lang = resolveLang(searchParams.get('lang'));
  const t = T[lang];

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);
  const [slotInfo, setSlotInfo] = useState(null);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [booked, setBooked] = useState(false);
  const [seriesResult, setSeriesResult] = useState(null);

  const customerToken = localStorage.getItem('portal_token');

  useEffect(() => {
    if (token) {
      loadSlotInfo();
    } else {
      setError(t.errInvalid);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSlotInfo = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/slot/${token}`);
      setSlotInfo(response.data.slot);
      setServiceInfo(response.data.service);
    } catch (error) {
      if (error.response?.status === 404) {
        setError(t.errInvalid);
      } else if (error.response?.status === 400) {
        setError(error.response.data?.detail || t.errExpired);
      } else {
        setError(t.errLoad);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!customerToken) {
      localStorage.setItem('redirect_after_login', window.location.href);
      navigate('/portal/login');
      return;
    }
    setBooking(true);
    try {
      await axios.post(
        `${API}/waitlist/book-slot/${token}`,
        {},
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      setBooked(true);
      toast.success(t.toastBooked);
      setTimeout(() => navigate('/portal'), 3000);
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.detail || t.errTaken);
      } else {
        toast.error(t.errBooking);
      }
    } finally {
      setBooking(false);
    }
  };

  const handleSeriesBooking = async () => {
    if (!customerToken) {
      localStorage.setItem('redirect_after_login', window.location.href);
      navigate('/portal/login');
      return;
    }
    setBooking(true);
    try {
      const response = await axios.post(
        `${API}/waitlist/book-slot/${token}/series`,
        {},
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      setSeriesResult(response.data);
      setBooked(true);
      toast.success(t.toastSeries);
      setTimeout(() => navigate('/portal'), 4000);
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.detail || t.errSeriesGone);
      } else {
        toast.error(t.errSeriesBooking);
      }
    } finally {
      setBooking(false);
    }
  };

  const localWeekday = (wdDe) =>
    lang === 'de' ? wdDe : (WEEKDAY_I18N[wdDe] || {})[lang] || wdDe;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t.locale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' });
  };

  const seriesPattern = slotInfo?.series_info
    ? t.every(localWeekday(slotInfo.series_info.weekday), slotInfo.series_info.time)
    : t.seriesFallback;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">{t.notAvailable}</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button
              onClick={() => navigate('/portal')}
              className="bg-yellow-500 text-black hover:bg-yellow-400"
            >
              {t.toPortal}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booked) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              {seriesResult ? t.bookedSeriesTitle : t.bookedTitle}
            </h2>
            <p className="text-gray-400 mb-2">
              {seriesResult
                ? t.bookedSeriesText(seriesResult.booked_count, serviceInfo?.name)
                : t.bookedText(serviceInfo?.name)}
            </p>
            {seriesResult?.skipped_count > 0 && (
              <p className="text-sm text-orange-400 mb-2" data-testid="series-skipped-info">
                {t.skipped(seriesResult.skipped_count)}
              </p>
            )}
            <p className="text-yellow-500 font-medium mb-6">
              {seriesResult && slotInfo?.series_info
                ? seriesPattern
                : t.at(formatDate(slotInfo?.slot_datetime), formatTime(slotInfo?.slot_datetime))}
            </p>
            <p className="text-sm text-gray-500 mb-4">{t.redirect}</p>
            <Button
              onClick={() => navigate('/portal')}
              variant="outline"
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
            >
              {t.nowToPortal}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-yellow-500" />
          </div>
          <CardTitle className="text-2xl text-white">{t.title}</CardTitle>
          <p className="text-gray-400 text-sm mt-2">{t.subtitle}</p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Slot Details */}
          <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t.date}</p>
                <p className="text-white font-medium">{formatDate(slotInfo?.slot_datetime)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t.time}</p>
                <p className="text-white font-medium">
                  {formatTime(slotInfo?.slot_datetime)}{t.timeSuffix}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t.service}</p>
                <p className="text-white font-medium">
                  {serviceInfo?.name || slotInfo?.service_name}
                </p>
                {serviceInfo?.duration_minutes && (
                  <p className="text-xs text-gray-500">
                    {serviceInfo.duration_minutes} {t.minutes}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Urgency Notice */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
            <p className="text-orange-400 text-sm text-center">{t.urgency}</p>
          </div>

          {/* 07/2026: Serien-Angebot */}
          {slotInfo?.series_occurrences?.length >= 2 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4" data-testid="series-offer-box">
              <p className="text-yellow-400 font-semibold text-sm mb-1">{t.seriesTitle}</p>
              <p className="text-gray-300 text-sm">
                {t.seriesText(seriesPattern, slotInfo.series_occurrences.length)}
              </p>
            </div>
          )}

          {/* Login Notice */}
          {!customerToken && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-400 text-sm text-center">{t.loginNotice}</p>
            </div>
          )}

          {/* Book Button */}
          <Button
            onClick={handleBooking}
            disabled={booking}
            data-testid="book-single-slot-btn"
            className="w-full bg-yellow-500 text-black hover:bg-yellow-400 h-12 text-lg font-semibold"
          >
            {booking ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {t.bookingBusy}
              </>
            ) : customerToken ? (
              slotInfo?.series_occurrences?.length >= 2 ? t.onlyThis : t.bookNow
            ) : (
              t.loginAndBook
            )}
          </Button>

          {/* 07/2026: Serie mit einem Klick buchen */}
          {slotInfo?.series_occurrences?.length >= 2 && (
            <Button
              onClick={handleSeriesBooking}
              disabled={booking}
              data-testid="book-series-btn"
              className="w-full bg-zinc-100 text-black hover:bg-white h-12 text-lg font-semibold border-2 border-yellow-500"
            >
              {booking ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t.bookingBusy}
                </>
              ) : (
                t.bookSeries(slotInfo.series_occurrences.length)
              )}
            </Button>
          )}

          {/* Valid Until */}
          {slotInfo?.valid_until && (
            <p className="text-xs text-gray-500 text-center">
              {t.validUntil} {new Date(slotInfo.valid_until).toLocaleString(t.locale)}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SpontanBookingPage;
